import { Box } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import KeyboardDoubleArrowDownOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowDownOutlined";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import MiddleEllipsis from "react-middle-ellipsis";
import Text from "./text/Text";
import Button from "./button/Button";
import {
  lnurl,
  loadingMsg,
  paymentFailSubMessage,
  redemptionFailSubMessage,
  sats,
  sendWithHighFee,
  XAUT,
} from "./messages";
import {
  btcToSatsAmount,
  callAPIInterface,
  displaySelectedCurrencyAmount,
  generateAmountToPass,
  getCurrencyAmount,
  handleAmountChangeCommon,
  payRequest,
  withdrawRequest,
  lightningLabel,
  onChainLabel,
  ethereumLabel,
  tronLabel,
  stableCoins,
  solanaLabel,
  tonLabel,
} from "./constants";
import { useSession } from "../hooks/useSession";
import TruncatedTextTooltip from "./TruncatedTextTooltip";
import EnterAmountBox from "./EnterAmountBox";
import FeesComponentCommon from "./FeesComponentCommon";
import InsufficientBalanceMsgBox from "./InsufficientBalanceMsgBox";
import PaymentFailModal from "./PaymentFailModal";
import CaptchaModal from "./CaptchaModal";
import CustomDivider from "./divider/Divider";
import ApplyCouponCode from "./ApplyCouponCode";
import SelectPayVia from "./SelectPayVia";
import BackdropLoader from "./backdropLoader/BackdropLoader";
import useExchangeAndSpeedFeeHook from "../hooks/useExchangeAndSpeedFeeHook";
import useBalanceAndPaymentMethods from "../hooks/useBalanceAndPaymentMethods";
import useEstimatedFees from "../hooks/useEstimatedFees";
import useCouponCodes from "../hooks/useCouponCodes";
import useSendButtonCalculation from "../hooks/useSendButtonCalculation";
import useSendPayment from "../hooks/useSendPayment";
import useTransactionMinMaxAmount from "../hooks/useTransactionMinMaxAmount";

function SendViaSection({
  isFromBrowser,
  data,
  amount,
  setAmount,
  setIsSendModalOpen,
  isSendMax,
  setIsSendMax,
  noteFromURL,
  currency,
  setCurrency,
  currencyExchangeRate,
  selectedContact,
}) {
  const navigate = useNavigate();
  const { session } = useSession();
  const { walletCurrency, showBtcAmount, payViaMethods } = useSelector(
    (state) => state.common
  );

  const { code: targetedCurrency, symbol: targetedCurrencySymbol } = {
    ...walletCurrency,
  };

  const [convertedAmount, setConvertedAmount] = useState(null);
  const [note, setNote] = useState(null);
  const [networkFeeAmount, setNetworkFeeAmount] = useState(0);
  const [conversionFeeAmount, setConversionFeeAmount] = useState(0);
  const [balance, setBalance] = useState(null);
  const [networkFeeLoader, setNetworkFeeLoader] = useState(true);
  const [exchangeRate, setExchangeRate] = useState(1);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [withdrawDetails, setWithdrawDetails] = useState(null);
  const [withdrawStatus, setWithdrawStatus] = useState("");
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);
  const [isShowErrorModal, setIsShowErrorModal] = useState(true);
  const [checkExchange, setCheckExchange] = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const [paymentFailedMsg, setPaymentFailedMsg] = useState(
    paymentFailSubMessage
  );
  const [buttonColor, setButtonColor] = useState(null);
  const [showFeesSection, setShowFeesSection] = useState(false);
  const [isRedemptionFail, setRedemptionFail] = useState(false);
  const [errorResponse, setErrorResponse] = useState(
    JSON.stringify({
      detail: "withdraw not working. Insufficient balance.",
    })
  );
  const [sendMaxAmount, setSendMaxAmount] = useState(null);
  const [speedFee, setSpeedFee] = useState(0);
  const [fixedFee, setFixedFee] = useState(0);
  const [calculatedSpeedFees, setCalculatedSpeedFees] = useState(0);
  const [openCaptchaModal, setOpenCaptchaModal] = useState(false);
  const [isNodeRestricted, setIsNodeRestricted] = useState(false);
  const [isSelectedAmountOrNote, setIsSelectedAmountOrNote] =
    useState("amount");
  const [couponCodes, setCouponCodes] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isPayViaOpen, setIsPayViaOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [minTransactionAmt, setMinTransactionAmt] = useState(0);
  const [maxTransactionAmt, setMaxTransactionAmt] = useState(0);

  const queryParam = `limit=${10}`;
  const accountCountry = session?.account?.account?.country;
  const accountId = session?.account?.account?.id;

  const isLightningMethod = data?.withdraw_method === lightningLabel;
  const isOnchainMethod = data?.withdraw_method === onChainLabel;
  const isEthereumMethod = data?.withdraw_method === ethereumLabel;
  const isTronMethod = data?.withdraw_method === tronLabel;
  const isSolanaMethod = data?.withdraw_method === solanaLabel;
  const isLnUrlMethodSend =
    data?.withdraw_method === lnurl && data?.tag === payRequest;
  const isLnUrlMethodReceive =
    data?.withdraw_method === lnurl && data?.tag === withdrawRequest;
  const isTonMethod = data?.withdraw_method === tonLabel;
  const isCommonMethod =
    isEthereumMethod || isTronMethod || isSolanaMethod || isTonMethod;

  const lightningWithLn =
    isLightningMethod || isLnUrlMethodSend || isLnUrlMethodReceive;

  const isLightningStableCoin =
    isLightningMethod && stableCoins.includes(currency);

  const isPayViaAvailable = !isLnUrlMethodReceive;

  const showCouponCodes = showFeesSection && !isLnUrlMethodReceive;

  const isAmountEditable =
    (isLightningMethod && data?.amount === 0) ||
    (isOnchainMethod && currency === sats) ||
    (isLnUrlMethodSend && data?.min_sendable !== data?.max_sendable) ||
    (isLnUrlMethodReceive &&
      data?.min_withdrawable !== data?.max_withdrawable) ||
    isCommonMethod;

  const textSize = isFromBrowser ? 16 : 14;
  // calculate exchange rate and speed fees
  useExchangeAndSpeedFeeHook({
    amount,
    currency,
    targetedCurrency,
    checkExchange,
    showBtcAmount,
    lightningWithLn,
    isOnchainMethod,
    isEthereumMethod,
    isTronMethod,
    isSolanaMethod,
    isTonMethod,
    isLnUrlMethodReceive,
    setSpeedFee,
    setFixedFee,
    setButtonLoader,
    setExchangeRate,
    setConvertedAmount,
  });

  // get balance and payment methods array
  useBalanceAndPaymentMethods({
    currency,
    data,
    isLightningMethod,
    isLnUrlMethodSend,
    payViaMethods,
    setBalanceData,
    setBalance,
    setPaymentMethods,
  });

  // calculate estimate fees and speed fee calculation
  useEstimatedFees({
    amount,
    currency,
    data,
    speedFee,
    fixedFee,
    balance,
    showBtcAmount,
    isOnchainMethod,
    isLightningMethod,
    isLnUrlMethodSend,
    isLnUrlMethodReceive,
    isEthereumMethod,
    isTronMethod,
    isSolanaMethod,
    isLightningStableCoin,
    isTonMethod,
    setCalculatedSpeedFees,
    setNetworkFeeLoader,
    setNetworkFeeAmount,
    setConversionFeeAmount,
  });

  // get coupon codes
  const { validateCoupon } = useCouponCodes({
    amount,
    data,
    currency,
    session,
    showFeesSection,
    queryParam,
    appliedCoupon,
    showCouponCodes,
    setCouponCodes,
    setAppliedCoupon,
    setButtonLoader,
    setNetworkFeeLoader,
  });

  // For send button label and color
  const { sendCalculations } = useSendButtonCalculation({
    amount,
    currency,
    balance,
    minTransactionAmt,
    maxTransactionAmt,
    sendMaxAmount,
    showBtcAmount,
    networkFeeAmount,
    calculatedSpeedFees,
    isLnUrlMethodReceive,
    isLnUrlMethodSend,
    isEthereumMethod,
    isTronMethod,
    isSolanaMethod,
    isTonMethod,
    isLightningMethod,
    isOnchainMethod,
    setButtonColor,
  });

  const { handleSubmit, handleSendPayment } = useSendPayment({
    amount,
    currency,
    note,
    data,
    accountId,
    appliedCoupon,
    selectedContact,
    withdrawDetails,
    isLnUrlMethodReceive,
    showBtcAmount,
    setErrorResponse,
    withdrawStatus,
    setOpenCaptchaModal,
    setButtonLoader,
    setPaymentFailedMsg,
    setRedemptionFail,
    setIsPaymentFailed,
    setIsShowErrorModal,
    setWithdrawDetails,
    setWithdrawStatus,
    setIsNodeRestricted,
    setIsSendModalOpen,
    setIsPaymentProcessing,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchSendMaxAmount = async () => {
    try {
      const response = await callAPIInterface(
        "GET",
        "/withdraws/lightning-max-amount"
      );
      setSendMaxAmount(getCurrencyAmount(response?.amount, currency));
    } catch (err) {}
  };

  useEffect(() => {
    if (amount > balance && !isLnUrlMethodReceive) {
      setShowFeesSection(false);
    } else if (isLightningMethod) {
      const minMount = isLightningStableCoin ? 0.5 : 1;
      const minRequired = displaySelectedCurrencyAmount(minMount);
      setShowFeesSection(amount >= minRequired);
    } else if (isOnchainMethod) {
      const minRequired = displaySelectedCurrencyAmount(1000);
      setShowFeesSection(
        currency === sats ? amount >= minRequired : amount > 0
      );
    } else if (isLnUrlMethodSend) {
      let minMount = stableCoins.includes(currency) ? 0.5 : 1;
      minMount = Math.max(minTransactionAmt, minMount);

      let minRequired =
        currency === sats ? displaySelectedCurrencyAmount(minMount) : minMount;
      setShowFeesSection(amount >= minRequired);
    } else if (isLnUrlMethodReceive) {
      setShowFeesSection(true);
    } else if (isCommonMethod) {
      setShowFeesSection(amount > 0);
    }
  }, [amount, data, balance, currency]);

  useEffect(() => {
    if (isSendMax) {
      handleSendMaxAmount();
    }
  }, [isSendMax]);

  const handleAmountChange = (sendAmount, checkExchange) => {
    handleAmountChangeCommon(
      sendAmount,
      checkExchange,
      exchangeRate,
      currency,
      setAmount,
      setConvertedAmount
    );

    setButtonColor(null);
  };

  const handleChangeCurrency = () => {
    setCheckExchange(!checkExchange);
  };

  const handleCaptchaCode = () => {
    handleSendPayment(true);
  };

  const handleSendMaxAmount = () => {
    setAppliedCoupon(null);
    handleAmountChange(sendMaxAmount?.toString(), false);
    setAppliedCoupon(null);
  };

  //set min max amount for Ln url send and receive
  useTransactionMinMaxAmount({
    data,
    currency,
    currencyExchangeRate,
    isOnchainMethod,
    isLnUrlMethodSend,
    isLnUrlMethodReceive,
    setMaxTransactionAmt,
    setMinTransactionAmt,
    setIsSendMax,
    setSendMaxAmount,
  });

  const displayAddress = useMemo(() => {
    if (isLnUrlMethodSend) {
      return `Pay to ${data?.payee}`;
    } else if (isLnUrlMethodReceive) {
      return data?.payee !== ""
        ? `Redeem from ${data?.payee}`
        : data?.withdraw_request;
    } else {
      return data?.withdraw_request;
    }
  }, [data]);

  const enterAmountBoxProps = {
    isFromBrowser,
    checkExchange,
    currentAmount: amount,
    isAmountEditable,
    currency,
    currentConvertedAmount: convertedAmount,
    currentNote: note,
    setCurrentNote: setNote,
    handleChangeCurrency: handleChangeCurrency,
    handleAmountChange: (e) => {
      handleAmountChange(e.target.value, checkExchange);
      setIsSendMax(false);
    },
    from: "withdraw",
    isSelectedAmountOrNote,
    setIsSelectedAmountOrNote,
  };

  const handlePaymentFailModalClose = () => {
    setIsShowErrorModal(false);
    setIsPaymentFailed(false);
    setIsPaymentProcessing(false);
    setRedemptionFail(false);
    setWithdrawDetails(null);
    setWithdrawStatus("");
  };

  const paymentFailProps = {
    isPaymentFailed,
    isNodeRestricted,
    isPaymentProcessing,
    isRedemptionFail,
    isFromBrowser,
    paymentFailedMsg,
    setIsSendModalOpen,
    isShowErrorModal,
    setIsShowErrorModal,
    errorResponse,
    handleModalClose: handlePaymentFailModalClose,
  };

  const feesProps = {
    amount,
    calculatedSpeedFees: Number(calculatedSpeedFees),
    isFromBrowser,
    isLightningMethod: isLightningMethod || isLnUrlMethodSend,
    isOnchainMethod,
    networkFeeAmount: Number(networkFeeAmount),
    conversionFeeAmount: Number(conversionFeeAmount),
    checkExchange,
    exchangeRate,
    convertedAmount,
    networkFeeLoader,
    isEthereumMethod,
    isTronMethod,
    isSolanaMethod,
    isTonMethod,
    speedFeesPercentage: speedFee,
    fixedFee: fixedFee,
  };

  const fees =
    currency === sats && !showBtcAmount
      ? Math.floor((amount * speedFee) / 100 + fixedFee)
      : ((amount * speedFee) / 100 + fixedFee)?.toFixed(
          showBtcAmount ? 8 : currency === XAUT ? 6 : 2
        );

  const lnURLReceiveProps = {
    min_amount: displaySelectedCurrencyAmount(data?.min_withdrawable),
    max_amount: displaySelectedCurrencyAmount(data?.max_withdrawable),
    calculatedSpeedFees: Number(fees),
    checkExchange,
    exchangeRate,
    speedFeesPercentage: speedFee,
    fixedFee: fixedFee,
  };

  const applyCouponCodeProps = {
    couponCodes,
    appliedCoupon,
    setAppliedCoupon,
    isFromBrowser,
    validateCoupon,
    isLoading: buttonLoader,
  };

  const payViaProps = {
    currency,
    isPayViaOpen,
    setIsPayViaOpen,
    paymentMethods,
    isFromBrowser,
    balanceData,
    withdrawData: data,
    setBalance,
    setButtonLoader,
    setCurrency,
    amount,
    setAmount,
    minTransactionAmt,
    setMinTransactionAmt,
    maxTransactionAmt,
    setMaxTransactionAmt,
    isShowHeading: true,
  };
  return (
    <>
      {(isPaymentFailed || isPaymentProcessing || isRedemptionFail) && (
        <PaymentFailModal {...paymentFailProps} />
      )}
      <BackdropLoader
        open={buttonLoader}
        alt={loadingMsg}
        text={loadingMsg}
        customClass="loading-in-auth"
      />
      <Box className="send-via-lightning-container">
        <Box className="inner-box">
          {/**Insufficient Balance message */}
          {sendCalculations?.isInsufficientBalance &&
            !buttonLoader &&
            !networkFeeLoader && (
              <InsufficientBalanceMsgBox
                isFromBrowser={isFromBrowser}
                isLightningMethod={isLightningMethod}
                balance={balance}
                exchangeRate={exchangeRate}
                checkExchange={checkExchange}
                targetedCurrency={targetedCurrency}
                targetedCurrencySymbol={targetedCurrencySymbol}
                currency={currency}
                amount={Number(amount)}
                calculatedSpeedFees={Number(calculatedSpeedFees)}
                networkFeeAmount={Number(networkFeeAmount)}
                showBtcAmount={showBtcAmount}
              />
            )}

          {isPayViaAvailable && <SelectPayVia {...payViaProps} />}

          <Box className="send-amount-box">
            {/**Enter Amount Box */}
            <EnterAmountBox {...enterAmountBoxProps} />
            <CustomDivider
              sx={{
                width: "38px",
                borderColor: "#F6F3F3",
                margin: "14px auto",
                borderWidth: "1px",
              }}
            />
            <Box display="flex" flexDirection="column" alignItems="center">
              {noteFromURL && (
                <TruncatedTextTooltip
                  textProps={{
                    className: "regular-text",
                  }}
                  textValue={noteFromURL}
                  cellWidth={isFromBrowser ? "325px" : "270px"}
                  fontSize={14}
                />
              )}
              <KeyboardDoubleArrowDownOutlinedIcon
                sx={{
                  color: "#2A67FF",
                  margin: "7px 0px",
                }}
              />
              <Text
                className="grey-text"
                size={textSize}
                font="regular"
                sx={{
                  width: "300px",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                }}
              >
                <MiddleEllipsis>
                  <span>{displayAddress}</span>
                </MiddleEllipsis>
              </Text>
            </Box>

            {/**It will be used in future. */}
            {/* <Box className="rewards-box">
            <Text
              className="default-text"
              font="regular"
              size={isFromBrowser ? 14 : 12}
            >
              {earnPointMsg("2")}
            </Text>
          </Box> */}
          </Box>

          {showCouponCodes && <ApplyCouponCode {...applyCouponCodeProps} />}

          {showFeesSection && (
            <Box className="fees-box">
              <FeesComponentCommon
                {...(isLnUrlMethodReceive ? lnURLReceiveProps : feesProps)}
                isLnUrlMethodReceive={isLnUrlMethodReceive}
                currency={currency}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box className={!isFromBrowser ? "footer" : "options-footer-btn"}>
        <Button
          fullWidth
          variant="contained"
          label={
            sendCalculations?.btnLabel
            // <TruncatedTextTooltip
            //   textProps={{ font: "semibold", size: "16px" }}
            //   textValue={sendCalculations?.btnLabel}
            //   cellWidth="290px"
            //   fontSize={12}
            // />
          }
          sx={{
            ...(!isFromBrowser && { margin: "14px 20px" }),
            ...(sendCalculations?.btnLabel === sendWithHighFee &&
              !networkFeeLoader && {
                backgroundColor: `${buttonColor} !important`,
              }),
          }}
          onClick={() => handleSubmit()}
          disabled={sendCalculations?.btnDisable || networkFeeLoader}
          loading={buttonLoader}
        />
      </Box>
      {openCaptchaModal && (
        <CaptchaModal
          isFromBrowser={isFromBrowser}
          isShowErrorModal={openCaptchaModal}
          setIsShowErrorModal={setOpenCaptchaModal}
          handleValidCaptcha={handleCaptchaCode}
        />
      )}
    </>
  );
}

export default SendViaSection;
